import I18n from "../I18n";
import soviPrompts from "./soviPrompts.json";

export const SOVI_OPEN_EVENT = "sovi:open-with-message";

export const SOVI_TESTIDS = {
    toolbarAiButton: "platform-toolbar-ai-button",
    newConversationButton: "ai-chat-new-conversation-button",
    messageInput: "ai-chat-message-input",
    chatPanel: "ai-chat-panel",
    sendButton: "ai-chat-send-button",
};

function getNativeTextareaSetter() {
    return Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
}

function getSoviInputWrapper() {
    return document.querySelector(`[data-testid="${SOVI_TESTIDS.messageInput}"]`) ?? null;
}

function getSoviTextarea() {
    return getSoviInputWrapper()?.querySelector("textarea") ?? null;
}

/**
 * Returns true when the AI panel is visually open.
 *
 * The SovosPlatformSlidingPanel uses a MUI persistent Drawer whose paper is
 * always mounted in the DOM (never unmounted when closed). MUI's Slide
 * transition sets `visibility: hidden` as an inline style on the paper when
 * the panel is closed, so reading that inline style is the only reliable
 * signal — child elements like the textarea or the new-conversation button
 * are present regardless of open state and cannot be used as indicators.
 */
function isSoviPanelOpen() {
    const paper = document.querySelector(
        `[data-testid="${SOVI_TESTIDS.chatPanel}"] .MuiDrawer-paper`,
    );
    return !!paper && paper.style.visibility !== "hidden";
}

/**
 * Imperatively clears any draft text from the Sovi textarea using React's
 * native setter so the controlled component registers the change.
 */
function clearSoviInput() {
    const textarea = getSoviTextarea();
    if (!textarea) return;
    const nativeSetter = getNativeTextareaSetter();
    if (nativeSetter) {
        nativeSetter.call(textarea, "");
        textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }
}

/**
 * Imperatively clicks the Sovi panel's Send button to submit the pre-filled
 * prompt. The button's React onClick reads from the controlled inputValue
 * state and bails out if the trimmed value is empty, so we wait until the
 * textarea reflects the expected text (a cheap proxy for "React state has
 * caught up with our synthetic input event") before clicking.
 */
function submitSoviInput(expectedText, onSent, attemptsLeft = 15) {
    const textarea = getSoviTextarea();
    const sendButton = document.querySelector(
        `[data-testid="${SOVI_TESTIDS.sendButton}"]`,
    );

    if (sendButton && textarea && textarea.value === expectedText) {
        sendButton.click();
        // The panel's sendMessage closes over aiContext at render time, so the
        // in-flight request keeps this context even after we release it here.
        // Clearing now prevents the per-page context from leaking into later messages.
        onSent?.();
    } else if (attemptsLeft > 0) {
        setTimeout(() => submitSoviInput(expectedText, onSent, attemptsLeft - 1), 100);
    } else if (process.env.NODE_ENV !== "production") {
        console.warn(
            `[Sovi] Could not click [data-testid="${SOVI_TESTIDS.sendButton}"] after all retries. ` +
            "The mosaic-react-platform toolbar API may have changed.",
        );
    }
}

function fillSoviInput(text, onSent, attemptsLeft = 15) {
    const textarea = getSoviTextarea();

    if (textarea) {
        const nativeSetter = getNativeTextareaSetter();
        if (nativeSetter) {
            nativeSetter.call(textarea, text);
            textarea.dispatchEvent(new Event("input", { bubbles: true }));
            submitSoviInput(text, onSent);
        }
    } else if (attemptsLeft > 0) {
        setTimeout(() => fillSoviInput(text, onSent, attemptsLeft - 1), 100);
    } else if (process.env.NODE_ENV !== "production") {
        console.warn(
            `[Sovi] Could not find [data-testid="${SOVI_TESTIDS.messageInput}"] textarea after all retries. ` +
            "The mosaic-react-platform toolbar API may have changed.",
        );
    }
}

function waitForResetAndFill(text, onSent, attemptsLeft = 20) {
    const textarea = getSoviTextarea();

    if (textarea && textarea.value === "") {
        fillSoviInput(text, onSent);
    } else if (attemptsLeft > 0) {
        setTimeout(() => waitForResetAndFill(text, onSent, attemptsLeft - 1), 100);
    } else if (process.env.NODE_ENV !== "production") {
        console.warn(
            "[Sovi] Textarea did not reset to empty after new conversation. " +
            "The mosaic-react-platform toolbar API may have changed.",
        );
    }
}

export function handleSoviOpenEvent(event, onSent) {
    const message = event.detail?.message ?? "";

    const isPanelOpen = isSoviPanelOpen();

    const toolbarAiButton = document.querySelector(
        `[data-testid="${SOVI_TESTIDS.toolbarAiButton}"]`,
    );

    if (!toolbarAiButton && process.env.NODE_ENV !== "production") {
        console.warn(
            `[Sovi] Could not find [data-testid="${SOVI_TESTIDS.toolbarAiButton}"]. ` +
            "The mosaic-react-platform toolbar API may have changed.",
        );
    }

    if (!isPanelOpen) {
        // Panel is closed — open it, then wait for the UI to settle before filling.
        toolbarAiButton?.click();
    }

    const triggerNewConversationAndFill = (attemptsLeft) => {
        const newConvButton = document.querySelector(
            `[data-testid="${SOVI_TESTIDS.newConversationButton}"]`,
        );

        if (newConvButton) {
            // Active conversation in progress: clear the draft, reset the thread,
            // then wait for the textarea to empty before writing the new prompt.
            clearSoviInput();
            newConvButton.click();
            waitForResetAndFill(message, onSent);
        } else if (isSoviPanelOpen()) {
            // Panel is open but already at the fresh/start state (no active
            // conversation), so there is nothing to reset — write directly.
            fillSoviInput(message, onSent);
        } else if (attemptsLeft > 0) {
            setTimeout(() => triggerNewConversationAndFill(attemptsLeft - 1), 100);
        } else if (process.env.NODE_ENV !== "production") {
            console.warn(
                `[Sovi] Could not find [data-testid="${SOVI_TESTIDS.newConversationButton}"] after all retries. ` +
                "The mosaic-react-platform toolbar API may have changed.",
            );
        }
    };

    setTimeout(() => triggerNewConversationAndFill(10), isPanelOpen ? 0 : 300);
}

/**
 * Returns the localised prompt string for a given key, replacing named
 * placeholders like {identifier} with the values supplied in `params`.
 *
 * Falls back to "en" when the current locale has no entry.
 */
export function getSoviPrompt(key, params = {}) {
    const locale = I18n.locale?.startsWith("pt") ? "pt" : "en";
    const template = soviPrompts[locale]?.[key] ?? soviPrompts["en"][key] ?? "";
    return template.replace(/\{(\w+)\}/g, (_, name) => {
        if (params[name] == null && process.env.NODE_ENV !== "production") {
            console.warn(`[getSoviPrompt] Missing param "${name}" for key "${key}"`);
        }
        return params[name] ?? "";
    });
}

/**
 * Builds the hidden ("page") AI context array sent alongside a visible Sovi
 * message. The structured params are passed to the backend as context instead
 * of being typed into the visible chat bubble. Returns undefined when there is
 * nothing to send.
 */
export function buildSoviContext(params = {}) {
    const lines = [];
    if (typeof window !== "undefined" && window.location?.href) {
        lines.push(`- URL: ${window.location.href}`);
    }
    for (const [key, value] of Object.entries(params)) {
        if (value != null && value !== "") lines.push(`- ${key}: ${value}`);
    }
    return lines.length ? [{ type: "page", content: lines.join("\n") }] : undefined;
}

/**
 * Dispatches a custom event that GlobalLayout intercepts to open the Sovi AI
 * panel and pre-fill the chat input. All DOM coupling lives in one place. The
 * optional context is hidden "page" context that reaches the toolbar as
 * aiContext rather than being typed into the visible textarea.
 */
export function openSoviWithMessage(message, context) {
    window.dispatchEvent(new CustomEvent(SOVI_OPEN_EVENT, { detail: { message, context } }));
}
